import { moment } from "@msuite/utils/date";
import z from "zod";
import { getProtocolPriorityCallbackTime } from "../../protocols";
import { generateId } from "../../strings";
import { ZodStringNullable } from "../zod-types/zod-string-nullable";

export const protocolTypes = ["general", "negotiation", "call-request", "meeting"] as const;

export const protocolSubjectTypes = ["project", "offer", "other"] as const;
export const protocolPriorityTypes = ["very-high", "high", "medium", "low", "custom"] as const;

export const protocolFormContentSectionSchema = z.object({
	id: z.string().default(generateId()),
	title: z.string().optional(),
	content: z.string().optional(),
});

export const protocolSubjectsSchema = z.object({
	type: z.enum(protocolSubjectTypes).optional(),
	value: z.string().optional(),
});

export const callRequestSchema = z.object({
	callRequestForId: z.string(),
	callRequestForIdSendNotification: z.boolean().default(false),
	priority: z.enum(protocolPriorityTypes).default("medium"),
	callRequestById: z.string(),
	callRequestByIdSendNotification: z.boolean().default(false),
	callbackTime: z.string().default(() => getProtocolPriorityCallbackTime("medium")),
});

export const protocolFormSchema = z
	.object({
		title: ZodStringNullable,
		type: z.enum(protocolTypes, { error: "Select protocol type" }),
		date: z.string(),
		startTime: z.string().nullable().default(null),
		endTime: z.string().nullable().default(null),
		location: z.string().optional(),
		subject: protocolSubjectsSchema.optional(),
		content: z.array(protocolFormContentSectionSchema),
		callRequestDetails: callRequestSchema.partial().optional(),
		recipients: z
			.array(
				z.object({
					type: z.enum(["user", "contactMember"]),
					id: z.string(),
				}),
			)
			.default([]),
	})
	.check((context) => {
		/** Subject Checks */
		if (context.value.subject?.type) {
			if (!context.value.subject.value) {
				context.issues.push({
					message: "Missing Subject Value",
					path: ["subject.value"],
					code: "custom",
					input: "none",
				});
			}
		}

		/** Call-Request Checks */
		if (context.value.type === "call-request") {
			if (!context.value.callRequestDetails?.callRequestForId) {
				context.issues.push({
					message: "Missing User",
					path: ["callRequestDetails.callRequestForUserId"],
					code: "custom",
					input: "none",
				});
			}
			if (!context.value.callRequestDetails?.callRequestById) {
				context.issues.push({
					message: "Missing Caller",
					path: ["callRequestDetails.callRequestById"],
					code: "custom",
					input: "none",
				});
			}
			if (!context.value.callRequestDetails?.callbackTime) {
				context.issues.push({
					message: "Missing Callback Time",
					path: ["callRequestDetails.callbackTime"],
					code: "custom",
					input: "none",
				});
			} else {
				const isBeforeNow = moment().isAfter(context.value.callRequestDetails.callbackTime);
				if (isBeforeNow) {
					context.issues.push({
						message: "Callback Time must be in the future",
						path: ["callRequestDetails.callbackTime"],
						code: "custom",
						input: "none",
					});
				}
			}
		} else if (context.value.type === "general") {
			/** General Protocol Checks */
			if (!context.value.title) {
				context.issues.push({
					message: "Missing Title",
					path: ["title"],
					code: "custom",
					input: "none",
				});
			}
		}
	});

export type ProtocolFormSchema = z.infer<typeof protocolFormSchema>;
export type ProtocolFormContentSectionSchema = z.infer<typeof protocolFormContentSectionSchema>;
